import React from 'react';
import PropTypes from 'prop-types';

import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {withUILevel} from '../../../lib/react/oe-higher-order-components';
import {OEMainMenuBtnConfigFactory, OEMainMenuButtonsPropType} from './oe-main-menu-model';
import OEMainMenuBar from './oe-main-menu-bar';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import {OEDefaultConfigFactory} from '../../oe-default-configs';
import OEToolbox from '../../../lib/tools/oe-toolbox';

export class OEMainMenu extends React.PureComponent {
    
    constructor(props) {
        super(props);

        this.buttons = this.constructButtons();

        this.maxWidthStep = 4;

        this.unitSize = {w: 54, h: 54};

        this.overMaxWidthStep = false;
        this.widthStep = 1;
        this.width = this.widthStep * this.unitSize.w;

        this.size = {w: this.width, height: 0};
        this.onSizeChanged = this.props.onSizeChanged;

        this.numVisibleTopButtons = -1;
        this.numVisibleBottomButtons = -1;

        this.state = {
            buttons: this.buttons,
            width: this.width, 
            overMaxWidthStep: false,
            style: {width: this.width.toString() + 'px'}
        };

        this.onResize = this.onResize.bind(this);
        this.onUnitSizeResize = this.onUnitSizeResize.bind(this);

        this.onRef = this.onRef.bind(this);
        this.onTopBarRef = this.onTopBarRef.bind(this);
        this.onBottomBarRef = this.onBottomBarRef.bind(this);

        this.onVisibleTopButtonsChanged = this.onVisibleTopButtonsChanged.bind(this);
        this.onVisibleBottomButtonsChanged = this.onVisibleBottomButtonsChanged.bind(this);
    }

    getSize()  {
        return this.size;
    }

    constructButtons(props) {
        props = props || this.props;
        let idCounter = 0;
        let fn = btn => Object.assign({id: idCounter++}, btn);
        
        return {
            top: props.buttons.top.map(fn),
            bottom: props.buttons.bottom.map(fn)
        };
    }

    componentWillReceiveProps(nextProps) {
        if(this.props.onSizeChanged !== nextProps.onSizeChanged) {
            this.onSizeChanged = nextProps.onSizeChanged;
            if(this.onSizeChanged) this.onSizeChanged(this, this.size);
        }

        if(!OEToolbox.arrayShallowEqual(this.props.buttons.top, nextProps.buttons.top) || !OEToolbox.arrayShallowEqual(this.props.buttons.bottom, nextProps.buttons.bottom)) {
            this.buttons = this.constructButtons(nextProps);
            this.setState({buttons: this.buttons});
        }
    }

    menuHeight(num, widthStep)   {
        let n = Math.ceil(num / widthStep);
        return n * this.unitSize.h;
    }

    layout()    {
        if(!this.elementRef || this.numVisibleTopButtons < 0 || this.numVisibleBottomButtons < 0) return;

        let numTop = this.numVisibleTopButtons;
        let numBottom = this.numVisibleBottomButtons;

        let height = this.elementRef.offsetHeight;
        let widthStep = this.widthStep;
        let overMaxWidthStep = false;
        let iterate = true;

        while(iterate)  {
            iterate = false;

            let newWidthStep = widthStep;
            let topHeight = this.menuHeight(numTop, widthStep);
            let bottomHeight = this.menuHeight(numBottom, widthStep);

            if(height < topHeight + bottomHeight)   {
                if(widthStep + 1 > this.maxWidthStep) overMaxWidthStep = true;
                newWidthStep = Math.min(widthStep + 1, this.maxWidthStep);
            } else if(widthStep > 1 && height > 50 + topHeight + bottomHeight)   {

                let widthStepTest = widthStep - 1;
                let topHeightTest = this.menuHeight(numTop, widthStepTest);
                let bottomHeightTest = this.menuHeight(numBottom, widthStepTest);

                if(!(height < topHeightTest + bottomHeightTest))    {
                    newWidthStep = widthStepTest;
                }
            }

            iterate = newWidthStep != widthStep;
            widthStep = newWidthStep;
        }

        if(this.overMaxWidthStep !== overMaxWidthStep)  {
            this.overMaxWidthStep = overMaxWidthStep;
            this.setState({overMaxWidthStep: overMaxWidthStep});
        }

        this.widthStep = widthStep;
        let newWidth = this.widthStep * this.unitSize.w;

        if(this.width !== newWidth)    {
            this.width = newWidth;
            this.setState({width: this.width, style: {width: this.width.toString() + 'px'}});
        }
    }

    updateTopButtonVisibility(deferred)   {
        let buttonVisibility = this.topButtonVisibility;
        this.topButtonVisibility = this.topBarRef ? this.topBarRef.getButtonVisibility() : null;
        if(OEToolbox.mapShallowEqual(buttonVisibility, this.topButtonVisibility))    return;
        this.updateButtonVisibility(deferred);
    }

    updateBottomButtonVisibility(deferred)   {
        let buttonVisibility = this.bottomButtonVisibility;
        this.bottomButtonVisibility = this.bottomBarRef ? this.bottomBarRef.getButtonVisibility() : null;
        if(OEToolbox.mapShallowEqual(buttonVisibility, this.bottomButtonVisibility))    return;
        this.updateButtonVisibility(deferred);
    }

    updateButtonVisibility(deferred)  {
        if(this.buttonVisibilityUpdatePending)  return;
        if(deferred)    {
            this.buttonVisibilityUpdatePending = true;
            setTimeout(() => { this.buttonVisibilityUpdatePending = false; this.updateButtonVisibility(); }, 50);
            return;
        }

        if(!this.topButtonVisibility || !this.bottomButtonVisibility)   {
            this.buttonVisibility = null;
            return;
        }
        
        let buttonVisibility = this.buttonVisibility;
        this.buttonVisibility = new Map([...this.topButtonVisibility, ...this.bottomButtonVisibility])
        if(OEToolbox.mapShallowEqual(buttonVisibility, this.buttonVisibility))    return;

        this.updateNumVisibleButtons();
    }
    
    updateNumVisibleButtons(deferred)   {
        if(!this.buttonVisibility)   return;
        let numVisibleTopButtons = 0;
        let numVisibleBottomButtons = 0;

        this.buttons.top.forEach(btn => {
            if(this.buttonVisibility.get(btn.id)) numVisibleTopButtons++;
        });

        this.buttons.bottom.forEach(btn => {
            if(this.buttonVisibility.get(btn.id)) numVisibleBottomButtons++;
        });

        if(numVisibleTopButtons !== this.numVisibleTopButtons || numVisibleBottomButtons !== this.numVisibleBottomButtons)    {
            this.numVisibleTopButtons = numVisibleTopButtons;
            this.numVisibleBottomButtons = numVisibleBottomButtons;
            this.layout();
        }
    }

    onResize(sender, size)  {
        if(OEToolbox.shallowEqual(this.size, size)) return;
        this.size = size;
        this.layout();
        if(this.onSizeChanged)  this.onSizeChanged(this, size);
    }

    onUnitSizeResize(sender, size)    {
        if(OEToolbox.shallowEqual(this.unitSize, size)) return;
        this.unitSize = size;
        this.layout();
    }

    onRef(ref) {
        if(typeof(this.props.elementRef) === 'function') {
            this.props.elementRef(ref); 
        }
        if(this.elementRef === ref) return;
        this.elementRef = ref;
        this.layout();
    }

    onTopBarRef(ref)    {
        this.topBarRef = ref;
        this.updateTopButtonVisibility();
    }

    onBottomBarRef(ref)    {
        this.bottomBarRef = ref;
        this.updateBottomButtonVisibility();
    }

    render() {
        const topButtons = !this.state.overMaxWidthStep ? this.state.buttons.top : this.state.buttons.top.concat(this.state.buttons.bottom);
        const bottomButtons = !this.state.overMaxWidthStep ? this.state.buttons.bottom : [];

        return (
            <div 
                id="main-menu"
                className={'main-menu main-menu-bg main-menu-text-color ' + (this.props.className ? this.props.className : '')}
                style={Object.assign({}, this.props.style, this.state.style)}
                ref={this.onRef}
            >
                <OEResizeObserver onResize={this.onResize}/>
                <OEResizeObserver className="unit-size-observer" onResize={this.onUnitSizeResize}/>

                <OEMainMenuBar
                    className="main-menu-top"
                    moduleId={this.props.moduleId}
                    buttonClassName="transparent-btn main-menu-button"
                    buttons={topButtons}
                    onVisibleButtonsChanged={this.onVisibleTopButtonsChanged}
                    ref={this.onTopBarRef}
                />
                <OEMainMenuBar
                    className="main-menu-bottom"
                    moduleId={this.props.moduleId}
                    buttonClassName="transparent-btn main-menu-button"
                    buttons={bottomButtons}
                    onVisibleButtonsChanged={this.onVisibleBottomButtonsChanged}
                    ref={this.onBottomBarRef}
                />
            </div>
        );
    }

    onVisibleTopButtonsChanged(sender) {
        // onVisibleTopButtonsChanged may be called inside the update cycle -> wait until until the cycle is finished,
        // otherwise obtained top/bottom button visibiliy can be inconsistent,
        // which may lead to an update feedback loop through layout
        this.updateTopButtonVisibility(true); 
    }

    onVisibleBottomButtonsChanged(sender) {
        this.updateBottomButtonVisibility(true);
    }
}

OEMainMenu.defaultProps = {
    moduleId: '',
    config: OEDefaultConfigFactory.mainMenu(),
    buttons: OEMainMenuBtnConfigFactory.buildStdButtons(false)
};

OEMainMenu.propTypes = {
    moduleId: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    config: PropTypes.shape({
        showInitially: PropTypes.oneOfType([PropTypes.bool, PropTypes.number])
    }).isRequired,
    buttons: PropTypes.shape({
        top: OEMainMenuButtonsPropType.isRequired,
        bottom: OEMainMenuButtonsPropType.isRequired
    }).isRequired,
    onSizeChanged: PropTypes.func
};

export default frefFromRef(withUILevel(frefToRef(OEMainMenu)));